import { inject, Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FsessionService } from 'src/app/servicios/session/fsession.service';
import { MomentService } from 'src/app/servicios/moment/moment.service';

/**
 * Pipe para formatear fechas con tres modos explícitos:
 *
 *   tz = 'UTC'   → La fecha es UTC, mostrarla en UTC sin conversión
 *   tz = 'LOCAL' → La fecha es UTC, convertirla a la zona del municipio (zona_js de sesión)
 *   tz = omitido → Mostrar los números tal como vienen en el string, sin crear Date,
 *                  sin aplicar ninguna zona (ni navegador, ni municipio)
 *
 * Formatos de entrada soportados:
 *   - ISO con Z:        2025-10-03T20:14:26.153Z   → muestra 20:14:26
 *   - ISO con offset:   2025-10-03T22:00:00+02:00  → muestra 22:00:00
 *   - ISO sin zona:     2025-10-03T22:14:26         → muestra 22:14:26
 *   - Solo fecha YYYY-MM-DD → devuelve DD-MM-YYYY sin hora
 *   - Solo fecha DD-MM-YYYY → devuelve tal cual
 *
 * En el modo omitido, la Z o el offset se ignoran completamente.
 * Se toman los números del string directamente. Nunca se crea un objeto Date.
 *
 * Uso en template:
 *   {{ row.FechaLocal | cityTimezoneDate }}                        → números crudos del string
 *   {{ row.FechaUTC   | cityTimezoneDate:'dd-MM-yyyy HH:mm:ss':'UTC'   }} → mostrar en UTC
 *   {{ row.FechaUTC   | cityTimezoneDate:'dd-MM-yyyy HH:mm:ss':'LOCAL' }} → convertir a municipio
 */
@Pipe({
  name: 'cityTimezoneDate',
  pure: true,
  standalone: true,
})
export class CityTimezoneDatePipe implements PipeTransform {
  private datePipe = inject(DatePipe);

  private readonly DEFAULT_FORMAT = 'dd-MM-yyyy HH:mm:ss';

  private static readonly RE_YMD        = /^(\d{4})-(\d{2})-(\d{2})$/;
  private static readonly RE_DMY        = /^\d{2}-\d{2}-\d{4}$/;
  private static readonly RE_ISO_Z      = /^\d{4}-\d{2}-\d{2}T[\d:.]+Z$/i;
  private static readonly RE_ISO_OFFSET = /^\d{4}-\d{2}-\d{2}T[\d:.]+[+\-]\d{2}:\d{2}$/;
  private static readonly RE_ISO_NOZONE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/;

  // Extrae los números de cualquier ISO independientemente de Z u offset al final
  private static readonly RE_ISO_PARTS  = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/;

  private fse = inject(FsessionService);
  private momentService = inject(MomentService);

  transform(
    value: any,
    format: string = this.DEFAULT_FORMAT,
    tz?: 'UTC' | 'LOCAL' | string,
    strict: boolean = false
  ): string | null {

    if (value == null || value === '') return null;

    try {
      const s = typeof value === 'string' ? value.trim() : null;

      // Número → no es fecha (ID, contador)
      if (typeof value === 'number') return String(value);

      // String solo numérico → no es fecha
      if (s && /^\d+$/.test(s)) return s;

      // YYYY-MM-DD → devolver como DD-MM-YYYY (sin zona, sin Date)
      if (s) {
        const matchYMD = s.match(CityTimezoneDatePipe.RE_YMD);
        if (matchYMD) return `${matchYMD[3]}-${matchYMD[2]}-${matchYMD[1]}`;

        // DD-MM-YYYY → devolver tal cual
        if (CityTimezoneDatePipe.RE_DMY.test(s)) return s;
      }

      // Sin tz → extraer números crudos del string, nunca crear Date
      // Evita que el navegador aplique su zona horaria
      if (!tz && s) {
        return this.formatFromString(s, format) ?? (strict ? null : s);
      }

      // Con tz → necesitamos un Date real para convertir la zona
      const date = this.parseToDate(value, strict);
      if (date === null) return strict ? null : String(value);

      return this.formatByMode(date, format, tz);

    } catch {
      return strict ? null : String(value);
    }
  }

  /**
   * Parsea el valor a un Date real.
   * Solo se llama cuando tz = 'UTC' o 'LOCAL' (se necesita conversión de zona).
   * Los casos sin zona ya se manejan antes en transform() con formatFromString().
   */
  private parseToDate(value: any, strict: boolean): Date | null {

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
      const s = value.trim();

      // ISO con Z → UTC explícito
      if (CityTimezoneDatePipe.RE_ISO_Z.test(s)) {
        const dt = new Date(s);
        return isNaN(dt.getTime()) ? null : dt;
      }

      // ISO con offset → Date respeta el offset automáticamente
      if (CityTimezoneDatePipe.RE_ISO_OFFSET.test(s)) {
        const dt = new Date(s);
        return isNaN(dt.getTime()) ? null : dt;
      }

      // ISO sin zona → construir como fecha local para evitar shifts del navegador
      const noZone = s.match(CityTimezoneDatePipe.RE_ISO_NOZONE);
      if (noZone) {
        const ms = noZone[7] ? Number((noZone[7] + '000').slice(0, 3)) : 0;
        const dt = new Date(
          Number(noZone[1]),
          Number(noZone[2]) - 1,
          Number(noZone[3]),
          Number(noZone[4]),
          Number(noZone[5]),
          Number(noZone[6]),
          ms
        );
        return isNaN(dt.getTime()) ? null : dt;
      }

      // Fallback genérico
      const dt = new Date(s);
      return isNaN(dt.getTime()) ? null : dt;
    }

    return null;
  }

  /**
   * Aplica el modo de zona horaria al Date y lo formatea.
   * Solo se llama cuando tz = 'UTC' o 'LOCAL'.
   */
  private formatByMode(date: Date, format: string, tz?: string): string | null {
    if (tz === 'UTC') {
      return this.datePipe.transform(date, format, 'UTC') ?? null;
    }

    if (tz === 'LOCAL') {
      const municipioTz = this.fse.optenerValor('zona_js') || 'Europe/Madrid';
      return this.formatWithIntl(date, format, municipioTz);
    }

    // La hora viene en local y se debe pasar a UTC, en el caso +02:00 -> +00:00
    if (tz == 'LOCAL_TO_UTC') {
      const municipioTz = "UTC";
      const momentDate = this.momentService.zoneToUtc(date.toISOString().replace('Z',''));
      return this.formatWithIntl(momentDate.toDate(), format, municipioTz);
    }

    // No debería llegar aquí, pero por seguridad
    return this.datePipe.transform(date, format, '+0000') ?? null;
  }

  /**
   * Formatea una fecha extrayendo los números directamente del string ISO,
   * sin crear un objeto Date. Ignora Z, offset o cualquier sufijo de zona.
   *
   * Ejemplos:
   *   '2025-10-03T20:14:26.153Z'    → '03-10-2025 20:14:26'
   *   '2025-10-03T22:00:00+02:00'   → '03-10-2025 22:00:00'
   *   '2025-10-03T22:14:26'         → '03-10-2025 22:14:26'
   */
  private formatFromString(s: string, format: string): string | null {
    const parts = s.match(CityTimezoneDatePipe.RE_ISO_PARTS);
    if (!parts) return null;

    return format
      .replace('yyyy', parts[1])
      .replace('YYYY', parts[1])
      .replace('MM',   parts[2])
      .replace('dd',   parts[3])
      .replace('HH',   parts[4])
      .replace('mm',   parts[5])
      .replace('ss',   parts[6]);
  }

  /**
   * Formatea un Date en una zona horaria IANA usando Intl,
   * sin modificar el objeto Date original.
   */
  private formatWithIntl(date: Date, format: string, ianaZone: string, toUTC = false): string | null {
    try {
      const parts = new Intl.DateTimeFormat('es-ES', {
        timeZone: ianaZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).formatToParts(date);

      const map: Record<string, string> = {};
      for (const p of parts) {
        if (p.type !== 'literal') map[p.type] = p.value;
      }

      return format
        .replace('yyyy', map['year'])
        .replace('YYYY', map['year'])
        .replace('MM',   map['month'])
        .replace('dd',   map['day'])
        .replace('HH',   map['hour'])
        .replace('mm',   map['minute'])
        .replace('ss',   map['second']);

    } catch {
      // Si la zona IANA no es válida, fallback sin zona
      return this.datePipe.transform(date, format) ?? null;
    }
  }
}